import { z } from 'zod'

/**
 * Runtime configuration, loaded from `/config.js` (rendered at container start, see
 * docker/entrypoint.sh). The bundle carries no environment-specific values, so one image is
 * promoted across environments by digest.
 */
const ConfigSchema = z.object({
  apiBaseUrl: z.string().url().transform((u) => u.replace(/\/+$/, '')),
  clerkPublishableKey: z.string().regex(/^pk_(test|live)_[A-Za-z0-9+/=_-]+$/, 'must be a Clerk publishable key'),
  environment: z.string().min(1).default('local'),
  version: z.string().min(1).default('dev'),
  sftpHost: z.string().min(1).optional(),
  /** Public MCP endpoint for project consumers (ADR-0011); the project page shows it when set. */
  mcpUrl: z.string().url().optional(),
  /** Optional support/help destination shown in the sidebar footer; hidden when unset. */
  helpUrl: z.string().url().optional(),
})

export type AppConfig = z.infer<typeof ConfigSchema>

declare global {
  interface Window {
    __SASTREX_CONFIG__?: unknown
  }
}

export type ConfigResult = { ok: true; config: AppConfig } | { ok: false; issues: string[] }

/** Parses the raw runtime config. Pure, so it is unit-testable. */
export function parseConfig(raw: unknown): ConfigResult {
  if (raw === undefined) return { ok: false, issues: ['config.js was not loaded'] }
  const parsed = ConfigSchema.safeParse(raw)
  if (parsed.success) return { ok: true, config: parsed.data }
  return {
    ok: false,
    issues: parsed.error.issues.map((i) => `${i.path.join('.') || 'config'}: ${i.message}`),
  }
}

export const configResult: ConfigResult = parseConfig(
  typeof window === 'undefined' ? undefined : window.__SASTREX_CONFIG__,
)

let current: AppConfig | null = configResult.ok ? configResult.config : null

/** Returns the validated config. Only call after the app has booted past the config gate. */
export function getConfig(): AppConfig {
  if (!current) throw new Error('Config not initialised')
  return current
}

/** Test seam. */
export function setConfigForTests(config: AppConfig): void {
  current = config
}
